'use client';

import * as React from "react";
import {useEffect, useState} from "react";
import {clsx} from "clsx";
import {WarningSvg} from "@/app/components/icons/WarningSvg";
import SuccessCheckSvg from "@/app/components/icons/SuccessCheckSvg";
import MessageErrorSvg from "@/app/components/icons/MessageErrorSvg";
import {APP_TOAST_DURATION_MS} from "@/lib/utils/contants/toast";

export interface ToastProps {
    title: string;
    description: string;
    type: "positive" | "negative" | "warning" | "info";
    duration?: number;
}

function AppToast(props: ToastProps) {
    const {title, description, type, duration = APP_TOAST_DURATION_MS} = props;

    // Seeded from document.hidden — a toast can be created while the tab is already
    // hidden, and sonner has its close timer paused from that moment. Starting at
    // `false` would drain the bar against a paused timer. Safe to read at first render:
    // sonner only builds this element client-side, at publish time.
    const [isDocumentHidden, setIsDocumentHidden] = useState(() => document.hidden);

    useEffect(() => {
        const onVisibilityChange = () => setIsDocumentHidden(document.hidden);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    return (
        <div
            className="relative overflow-hidden rounded-lg border-1 border-noble-black-500 bg-noble-black-600 w-full md:max-w-[364px] p-[16px] z-1000 flex items-center gap-[18px]">
            <div
                className={clsx({['text-electric-green']: type === 'positive'}, {['text-happy-orange-600']: type === 'warning'}, {['text-red-power-600']: type === 'negative'})}>
                {type === 'positive' ? <SuccessCheckSvg/> : null}
                {type === 'warning' ? <WarningSvg/> : null}
                {type === 'negative' ? <MessageErrorSvg/> : null}
            </div>
            <div
                className={clsx({['text-electric-green']: type === 'positive'}, {['text-happy-orange-600']: type === 'warning'}, {['text-red-power-600']: type === 'negative'}, 'font-semibold text-[14px] leading-none')}>
                <p className={'mb-[8px]'}>{title}</p>
                <p className="text-noble-black-400 font-normal">{description}</p>
            </div>
            {duration !== Infinity ? (
                <div className="absolute inset-x-0 bottom-0 h-[3px] bg-white/8">
                    <div
                        className="app-toast-progress h-full w-full rounded-full bg-[linear-gradient(90deg,#E5BF80_0%,#E8B964_55.21%,#EBB146_100%)]"
                        data-paused={isDocumentHidden}
                        style={{animationDuration: `${duration}ms`}}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default AppToast;
